import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@shared/lib/cn';
import styles from './Button.module.css';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: Variant;
  readonly size?: Size;
  readonly leftIcon?: ReactNode;
  readonly rightIcon?: ReactNode;
  readonly isLoading?: boolean;
}

/**
 * Single Button component. Variants/sizes via props (not separate components)
 * so callers can drive styling from data without conditional JSX trees.
 *
 * `type="button"` defaults so we never accidentally submit a form.
 */
export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    leftIcon,
    rightIcon,
    isLoading = false,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        styles.btn,
        styles[`v_${variant}`],
        styles[`s_${size}`],
        isLoading && styles.loading,
        className,
      )}
      disabled={disabled === true || isLoading}
      aria-busy={isLoading || undefined}
      {...rest}
    >
      {leftIcon !== undefined ? <span className={styles.icon}>{leftIcon}</span> : null}
      <span className={styles.label}>{children}</span>
      {rightIcon !== undefined ? <span className={styles.icon}>{rightIcon}</span> : null}
    </button>
  );
});
